import * as React from "react"
import * as Yup from "yup"
import { Form, FormElement } from "@modules/form/components/Form"
import {
  Button,
  InputConnectedField,
  StyledModalActions,
  useShowSuccessToast,
} from "gatsby-interface"
import {
  ui as uiText,
  gatsbyCloudCdnIntegrationConfig as text,
  errorAlert as errorText,
} from "@modules/locales/default.json"
import { useTriggerErrorAlert } from "@modules/ui/components/ErrorAlert"
import {
  HostingDetailsForSiteDocument,
  useAddCloudCdnDomainMutation,
} from "@modules/cdn/gatsbyCloudCdn/queries.generated"
import { FormattedMessage, commonMessageTags } from "@modules/locales"

type FormValues = {
  domain: string
}

const validationSchema = Yup.object().shape<FormValues>({
  domain: Yup.string()
    .required(uiText.messages.validationIsRequired)
    .matches(/^[.\-a-zA-Z0-9]*\.[a-zA-Z]{2,}$/, {
      message: text.messages.validationMustBeValidDomain,
    }),
})

export type AddDomainFormProps = {
  siteId: string
  onComplete: () => void
  onCancel: () => void
}

export function AddDomainForm({
  siteId,
  onComplete,
  onCancel,
}: AddDomainFormProps) {
  const showSuccessToast = useShowSuccessToast()
  const [addCloudCdnDomainMutation] = useAddCloudCdnDomainMutation()
  const [setError, errorAlert] = useTriggerErrorAlert()

  return (
    <Form<FormValues>
      initialValues={{
        domain: ``,
      }}
      validationSchema={validationSchema}
      onSubmit={values => {
        return addCloudCdnDomainMutation({
          variables: {
            siteId,
            domainName: values.domain,
          },
          refetchQueries: [
            {
              query: HostingDetailsForSiteDocument,
              variables: { siteId },
            },
          ],
        })
          .then(({ data }) => {
            const success = data?.addCloudCdnDomain?.success ?? false

            if (success) {
              onComplete()
              return showSuccessToast(
                <FormattedMessage<"domain", "strong">
                  message={text.messages.domainAdded}
                  values={{ domain: values.domain }}
                  tags={commonMessageTags}
                />
              )
            }

            const message =
              data?.addCloudCdnDomain?.message ||
              errorText.messages.somethingWentWrong
            setError({ message })
          })
          .catch(err => {
            return setError(err)
          })
      }}
    >
      {({ isSubmitting }) => {
        return (
          <FormElement applySpacing noValidate>
            <InputConnectedField
              label={text.labels.domainField}
              name="domain"
              required
            />
            {errorAlert}
            <StyledModalActions>
              <Button variant="SECONDARY" type="reset" onClick={onCancel}>
                {uiText.actions.cancel}
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                loading={isSubmitting}
                loadingLabel={uiText.messages.saving}
              >
                {uiText.actions.save}
              </Button>
            </StyledModalActions>
          </FormElement>
        )
      }}
    </Form>
  )
}
